import { useContext, useState } from "react";
import { DatePicker, Form, Input, Row, Space, Button, Tooltip } from "antd";
import { translate } from "../../../core";
import { AddressType, Contact, WebInfoType } from "../../contact/type/contact";
import { OrderEntryPhones } from "./OrderEntryPhones";
import { OrderFormLines } from "./OrderFormLines";
import { OrderEntryFooter } from "./OrderEntryFooter";
import { dateFormat } from "../../../core/until";
import { Order, OrderAddress, OrderEntry } from "../type";
import {
  PageContextValue,
  QueryPageableResponse,
  QueryPageableRequest,
} from "../../../core/type";
import { InputCard } from "../../inventory/component/InputCard";
import styles from "./Order.module.scss";
import { OrderEntryPayments } from "./OrderEntryPayments";
import { mapEntry } from "../model-mapper/order-map-entity-to-entry";
import { OrderFormAddresses } from "./OrderFormAddresses";
import { createAddressByCustomer } from "../model-mapper/create-address";
import { OrderFormRepairsDisplay } from "./OrderFormRepairsDisplay";
import { OrderFormWeddingRegistrySelect } from "./OrderFormWeddingRegistrySelect";
import { WeddingRegistry } from "../../wedding-registry/type";
import { ContactSelect } from "../../contact/component/ContactSelect";
import { OrderPageContext } from "./OrderPageContext";
import { OrderFormRepair } from "./OrderFormRepair";
import { gql } from "@apollo/client";
import { CopyToClipboard, EmailFormItem, ShowIf } from "../../../core/component";
import { CheckSquareOutlined, EditOutlined } from "@ant-design/icons";
import { OrderFormCustomerRelations } from "./OrderFormCustomerRelations";
import { ContactModal } from "../../contact/component/ContactModal";
import { ContactFindRequest } from "../../contact/type/contact.query";
import { useLazyQuery } from "@apollo/client";

export const OrderForm = ({ shipping }: { shipping: OrderAddress | undefined }) => {
  const [showRepair, setShowRepair] = useState<boolean>(false);
  const [selectedRepairVersion, setSelectedRepairVersion] = useState<string>();
  const [weddingRegistry, setWeddingRegistry] = useState<WeddingRegistry>();
  const { form, entity, onFormChange, save } =
    useContext<PageContextValue<Order, OrderEntry>>(OrderPageContext);
  const [customerId, setCustomerId] = useState<string>("");
  const [customer, setCustomer] = useState<Contact>();
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);

  const onAmountsChanged = () => {
    const entry = form?.getFieldsValue() as OrderEntry;
    mapEntry(entry, entity?.taxRate as number);
    form?.setFieldsValue(entry as any);
  };

  const onRepairSelect = (version: string) => {
    setSelectedRepairVersion(version);
    setShowRepair(true);
  };

  const handleCustomerChange = (customer?: Contact) => {
    const entry = form?.getFieldsValue() as OrderEntry;
    if (customer)
      entry.customerEmail =
        customer.webInfos.find((e) => e.type === WebInfoType.EmailAddress)
          ?.value ?? "";

    const phone = (customer?.phones ?? [])[0];
    entry.customerWork = phone?.number ?? "";
    entry.customerWorkCountryCode = phone?.countryCode ?? "";

    createAddressByCustomer(entry, customer);
    const shippingIndex = entry.addresses.findIndex(ad => ad?.type === AddressType.Shipping);
    if (shipping) {
      if (entry.addresses.length === 1) {
        entry.addresses.push(shipping);
      } else if (shippingIndex !== -1) {
        entry.addresses[shippingIndex] = shipping;
      }
    }

    form?.setFieldsValue(entry as any);
  };

  const handleSameAddressChange = (checked: boolean) => {
    const entry = form?.getFieldsValue() as OrderEntry;

    createAddressByCustomer(entry, customer);
    if (!checked) {
      const shippingIndex = entry.addresses.findIndex(ad => ad?.type === AddressType.Shipping);
      if (shippingIndex !== -1 && shipping) {
        entry.addresses[shippingIndex] = {
          ...shipping,
          theShipmentAddressIsTheSameAsBilling: false,
        };
      }
    }
    form?.setFieldsValue(entry as any);
  };

  const [findContacts] = useLazyQuery<
    QueryPageableResponse<Contact, "contactsFind">,
    QueryPageableRequest<ContactFindRequest>
  >(
    gql`
      query GetContact($request: ContactFindRequest!) {
        contactsFind(request: $request) {
          data {
            id
            contactId
            company
            firstName
            lastName
            group {
              id
              title
            }
            phones {
              type
              number
            }
            addresses {
              type
              city
              country
              street
              zipCode
              state
              line1
              line2
            }
            webInfos {
              type
              value
            }
            accountNumber
          }
        }
      }
    `
  );

  return (
    <>
      <Form
        onFieldsChange={onFormChange}
        layout="vertical"
        name="contentEntry"
        form={form}
        onFinish={save}
      >
        <OrderFormRepair
          open={showRepair}
          onClose={() => setShowRepair(false)}
          version={selectedRepairVersion}
          onChangeVersion={setSelectedRepairVersion}
          onAmountChange={onAmountsChanged}
        />

        <ShowIf condition={!!entity?.online} replacement={<></>}>
          <Space style={{ color: "#0564ee" }}>
            <CheckSquareOutlined style={{ fontSize: 20 }} />
            <label>{translate("online")}</label>
          </Space>
        </ShowIf>
        <InputCard contained className={styles["customerInfoCardWrapper"]}>
          <Row align="bottom" className={styles["customerInfoCard"]}>
            <Form.Item
              label={translate("order_number_abbr")}
              name="number"
              className="w-xsmall"
            >
              <Input disabled={true} />
            </Form.Item>
            <Form.Item
              label={
                <>
                  {translate("contact_id")}
                  <CopyToClipboard
                    text={customerId}
                    tooltip={translate("copy", "contact_id")}
                  />
                  <Tooltip>
                    <Button
                      type="text"
                      shape="circle"
                      icon={<EditOutlined />}
                      onClick={() => setOpenCreateDialog(true)}
                    />
                  </Tooltip>
                </>
              }
              name={["customer", "id"]}
            >
              <ContactSelect
                width={250}
                onCustomerChangedOrBound={(customer, isBound) => {
                  setCustomerId(customer?.contactId);
                  setCustomer(customer);

                  if (isBound) return;

                  handleCustomerChange(customer);
                }}
              />
            </Form.Item>
            <Form.Item
              label={translate("date")}
              name="date"
              rules={[
                {
                  required: true,
                  message: translate("missing_field_message", "date"),
                },
              ]}
              className="w-small"
            >
              <DatePicker format={dateFormat} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              label={translate("clerk")}
              name="clerk"
              rules={[
                {
                  required: true,
                  message: translate("missing_field_message", "clerk"),
                },
              ]}
            >
              <Input className="w-xxsmall" />
            </Form.Item>

            <OrderEntryPhones />

            <EmailFormItem label={translate("email")} name="customerEmail" />
            <Form.Item
              label={translate("registry_number")}
              name={["weddingRegistry", "id"]}
            >
              <OrderFormWeddingRegistrySelect onSelect={setWeddingRegistry} />
            </Form.Item>
          </Row>
        </InputCard>
        <OrderFormAddresses shipping={shipping} onSameAddressChange={(checked) => handleSameAddressChange(checked)} />
        <OrderEntryFooter onChanged={onAmountsChanged} />
        <OrderFormLines
          key={selectedRepairVersion}
          onSelectRepair={onRepairSelect}
          onAmountsChanged={onAmountsChanged}
          weddingRegistry={weddingRegistry}
        />
        <OrderEntryPayments onAmountsChanged={onAmountsChanged} />
        <OrderFormRepairsDisplay onSelectRepair={onRepairSelect} />
        <OrderFormCustomerRelations customerId={customerId} />
      </Form>
      <ContactModal
        entity={customer}
        open={openCreateDialog}
        close={() => setOpenCreateDialog(false)}
        onSaveCompleted={async (id: number) => {
          setOpenCreateDialog(false);
          const { data } = await findContacts({
            variables: {
              request: {
                take: 1,
                skip: 0,
                id,
              },
            },
          });
          const [contact] = data?.contactsFind.data ?? [];
          setCustomer(contact);
          handleCustomerChange(contact);
        }}
      />
    </>
  );
};
